"use client";

import { useMemo, useState, useEffect, useCallback } from "react";
import type { ColumnDef, SortState, SortDirection } from "./types";
import { plainFormattedValue } from "./formatters";
import { readJSON, writeJSON } from "../utils/storage";

interface PersistedColumnPrefs {
  order: string[];
  hidden: string[];
}

function storageKeyFor(storageKey: string): string {
  // key 含頁面 id（呼叫端傳入的 storageKey 本身即頁面 id），對齊裁決 6-3
  return `ccheng-datatable:${storageKey}`;
}

/**
 * 第 20 輪嚴重修正第 1、2 條：欄位「結構性鎖定」判斷的單一事實來源。
 * 序號欄（kind:"serial"）與 lockedVisible 欄比照舊站 DynaGrid SerialColumn——
 * 不只可見性不給改，位置也固定原位；可見性與順序一律不接受使用者覆寫（裁決 6-3）。
 */
export function isColumnLocked<T>(col: ColumnDef<T>): boolean {
  return (col.lockedVisible ?? false) || col.kind === "serial";
}

/** 依 localStorage 偏好 + 欄位預設，算出目前的顯示欄位（依序）。 */
function applyColumnPrefs<T>(columns: ColumnDef<T>[], prefs: PersistedColumnPrefs | undefined): ColumnDef<T>[] {
  if (!prefs) return columns;
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const ordered: ColumnDef<T>[] = [];
  const seen = new Set<string>();
  for (const key of prefs.order) {
    const col = byKey.get(key);
    if (col) {
      ordered.push(col);
      seen.add(key);
    }
  }
  // 新增欄位（規格更新後、使用者尚未儲存過偏好）接在最後
  for (const c of columns) {
    if (!seen.has(c.key)) ordered.push(c);
  }
  // 第 20 輪嚴重修正第 2 條：套用 order 偏好後，鎖定欄（isColumnLocked）一律強制
  // 搬回其預設索引位置——除了 moveColumn() 擋掉新的交換操作外，「修正前已寫入
  // localStorage 的錯亂順序」也要在重新載入時自我復原，不能只擋新操作
  // （比照第 18 輪對 hidden 偏好的既有壞資料復原精神；裁決 6-3）。
  let restored = ordered;
  if (columns.some((c) => isColumnLocked(c))) {
    const unlockedQueue = ordered.filter((c) => !isColumnLocked(c));
    let qi = 0;
    restored = columns.map((orig) => (isColumnLocked(orig) ? orig : unlockedQueue[qi++]));
  }
  // 第 18 輪一般修正第 1 條：序號欄（kind:"serial"）與 lockedVisible 欄一律不吃
  // localStorage 的 hidden 偏好——除了防止新的隱藏操作（ColumnSettingsModal 已鎖），
  // 也讓「修正前已被勾掉而寫進偏好」的序號欄在重新載入時自動復原顯示（裁決 6-3）。
  return restored.map((c) =>
    isColumnLocked(c) ? c : { ...c, visible: !prefs.hidden.includes(c.key) },
  );
}

/**
 * @param serverMode 伺服端分頁模式：**完全不在前端過濾/排序/分頁**。
 *   rows 就是伺服器給的那一頁，原樣輸出。欄位個人化（順序/顯示）不受影響——
 *   那本來就是純前端的偏好，與資料在哪裡算無關。
 */
export function useDataTableState<T>(
  rawColumns: ColumnDef<T>[],
  rows: T[],
  storageKey: string | undefined,
  serverMode = false,
) {
  const persistKey = storageKey ? storageKeyFor(storageKey) : undefined;

  const [columns, setColumns] = useState<ColumnDef<T>[]>(() => {
    const prefs = persistKey ? readJSON<PersistedColumnPrefs>(persistKey) : undefined;
    return applyColumnPrefs(rawColumns, prefs);
  });

  // 上游欄位定義變動（例如切換頁面）時，重新套用個人化並保留使用者偏好
  useEffect(() => {
    const prefs = persistKey ? readJSON<PersistedColumnPrefs>(persistKey) : undefined;
    setColumns(applyColumnPrefs(rawColumns, prefs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawColumns, persistKey]);

  const persist = useCallback(
    (next: ColumnDef<T>[]) => {
      if (!persistKey) return;
      writeJSON(persistKey, {
        order: next.map((c) => c.key),
        hidden: next.filter((c) => c.visible === false).map((c) => c.key),
      } satisfies PersistedColumnPrefs);
    },
    [persistKey],
  );

  const setColumnVisible = useCallback(
    (key: string, visible: boolean) => {
      setColumns((prev) => {
        const next = prev.map((c) => (c.key === key ? { ...c, visible } : c));
        persist(next);
        return next;
      });
      // 第 15 輪嚴重修正第 1 條：隱藏欄位即視為放棄其篩選條件——否則篩選輸入框
      // 已隨欄位消失，條件卻仍套用於 filteredSortedRows/total，管理者只見「共 N 筆」
      // 異常偏低而無從得知肇因（§9.12 每欄篩選 × 裁決 6-3 欄位個人化疊加缺口）。
      if (!visible) {
        setFilters((prev) => {
          if (!(key in prev)) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setPage(1);
      }
    },
    [persist],
  );

  const moveColumn = useCallback(
    (key: string, direction: -1 | 1) => {
      setColumns((prev) => {
        const idx = prev.findIndex((c) => c.key === key);
        const swapWith = idx + direction;
        if (idx < 0 || swapWith < 0 || swapWith >= prev.length) return prev;
        // 第 20 輪嚴重修正第 1 條：交換雙方任一為鎖定欄（isColumnLocked：序號欄／
        // lockedVisible）即一律拒絕交換（no-op、不 persist）——不只鎖序號欄自己的
        // ↑↓ 鍵，相鄰資料欄也不得從對側把序號欄擠開；比照 applyColumnPrefs() 對
        // 可見性「結構上不接受使用者覆寫」的原則，同步套用到順序（裁決 6-3）。
        if (isColumnLocked(prev[idx]) || isColumnLocked(prev[swapWith])) return prev;
        const next = prev.slice();
        [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const resetColumns = useCallback(() => {
    setColumns(rawColumns);
    if (persistKey) writeJSON(persistKey, { order: rawColumns.map((c) => c.key), hidden: [] });
  }, [rawColumns, persistKey]);

  // ---- 排序 / 篩選 / 分頁（v1：不持久化，屬瀏覽態） ----
  const [sort, setSort] = useState<SortState>({ key: null, direction: null });
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [globalSearch, setGlobalSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const toggleSort = useCallback((key: string) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      const nextDir: SortDirection = prev.direction === "asc" ? "desc" : prev.direction === "desc" ? null : "asc";
      return { key: nextDir ? key : null, direction: nextDir };
    });
  }, []);

  const setFilter = useCallback((key: string, value: string) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // 第 15 輪嚴重修正第 1 條：一鍵清除全部欄位篩選（供工具列「已套用 N 個篩選」
  // 摘要列使用，讓篩選狀態不論欄位顯示與否都保持可清除）。
  const clearAllFilters = useCallback(() => {
    setPage(1);
    setFilters({});
  }, []);

  const dataColumns = useMemo(() => columns.filter((c) => (c.kind ?? "data") === "data"), [columns]);

  const filteredSortedRows = useMemo(() => {
    let result = rows;

    // 伺服端分頁模式：條件與排序都在伺服器上算過了，這裡再算一次只會作用在「當頁」，
    // 產生「篩選看起來有效，其實只篩了 20 筆」的錯覺（見 types.ts ServerTableState）。
    if (serverMode) return result;

    if (globalSearch.trim()) {
      const needle = globalSearch.trim().toLowerCase();
      result = result.filter((row) =>
        dataColumns.some((c) => plainFormattedValue(c, row).toLowerCase().includes(needle)),
      );
    }

    const activeFilters = Object.entries(filters).filter(([, v]) => v !== "" && v !== undefined);
    if (activeFilters.length > 0) {
      result = result.filter((row) =>
        activeFilters.every(([key, filterValue]) => {
          const col = dataColumns.find((c) => c.key === key);
          if (!col) return true;
          const cellText = plainFormattedValue(col, row);
          if (col.filterOptions) return cellText === filterValue;
          return cellText.toLowerCase().includes(filterValue.toLowerCase());
        }),
      );
    }

    if (sort.key && sort.direction) {
      const col = dataColumns.find((c) => c.key === sort.key);
      if (col) {
        const dir = sort.direction === "asc" ? 1 : -1;
        result = result.slice().sort((a, b) => {
          const av = col.value ? col.value(a) : (a as Record<string, unknown>)[col.key];
          const bv = col.value ? col.value(b) : (b as Record<string, unknown>)[col.key];
          if (av === bv) return 0;
          if (av === null || av === undefined) return 1;
          if (bv === null || bv === undefined) return -1;
          if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
          return String(av).localeCompare(String(bv), "zh-Hant-TW") * dir;
        });
      }
    }

    return result;
  }, [rows, dataColumns, globalSearch, filters, sort, serverMode]);

  const total = filteredSortedRows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pageCount);

  const pageRows = useMemo(() => {
    if (serverMode) return filteredSortedRows; // 伺服器給的就是「這一頁」
    const start = (currentPage - 1) * pageSize;
    return filteredSortedRows.slice(start, start + pageSize);
  }, [filteredSortedRows, currentPage, pageSize, serverMode]);

  return {
    columns,
    setColumnVisible,
    moveColumn,
    resetColumns,
    sort,
    toggleSort,
    filters,
    setFilter,
    clearAllFilters,
    globalSearch,
    setGlobalSearch,
    page: currentPage,
    setPage,
    pageSize,
    setPageSize: (n: number) => {
      setPageSize(n);
      setPage(1);
    },
    pageCount,
    total,
    pageRows,
    filteredSortedRows,
  };
}
